import React from "react";
import { motion } from "framer-motion";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const SECTIONS = [
  {
    title: "1. Information We Collect",
    content: "When you apply to work with ApplyStrat, we collect personal information including your full name, email address, phone number, location, LinkedIn profile URL, work authorization status, target roles, and any notes you provide. We may also collect your resume if you send it to us directly via email.",
  },
  {
    title: "2. How We Use Your Information",
    content: "We use your information exclusively to provide our job application support service. This includes researching and submitting applications on your behalf through official company portals, optimizing your profile materials, communicating with you about your search, and improving our internal strategy and execution.",
  },
  {
    title: "3. Information Sharing",
    content: "We do not sell, rent, or share your personal information with third parties for marketing purposes. Your information is only submitted to third-party company portals as part of job applications, and only based on your explicit preferences and direction.",
  },
  {
    title: "4. Data Security",
    content: "We take reasonable technical and organizational measures to protect your personal information. All data is stored securely and access is limited to authorized team members who need it to perform our services.",
  },
  {
    title: "5. Data Retention",
    content: "We retain your information for as long as you are an active client. After your engagement ends, we may retain basic records for up to 12 months for operational and compliance purposes, unless you request earlier deletion.",
  },
  {
    title: "6. Your Rights",
    content: "You have the right to access, update, or request deletion of your personal information at any time. To exercise these rights, please contact us at hello@applystrat.com.",
  },
  {
    title: "7. Contact Us",
    content: "If you have any questions about this Privacy Policy or how we handle your information, please contact us at hello@applystrat.com.",
  },
];

const PrivacyPage = () => (
  <div data-testid="privacy-page" className="min-h-screen bg-white">
    <SEOHead
      title="Privacy Policy | ApplyStrat"
      description="Learn how ApplyStrat collects, uses, and protects your personal information when you use our tech job application support service."
      canonical="https://applystrat.com/privacy"
    />

    <section className="pt-36 pb-28 max-w-3xl mx-auto px-6">
      <motion.div {...fadeUp()}>
        <p className="section-label-orange mb-4">Legal</p>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-ink mb-4 leading-tight">Privacy Policy</h1>
        <p className="text-ink-secondary text-sm mb-12">Last updated: January 2025</p>
      </motion.div>

      <motion.div {...fadeUp(0.1)} className="space-y-4">
        {SECTIONS.map((section, i) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, ease, delay: i * 0.05 }}
            className="bg-surface-raised rounded-2xl p-7 hover:bg-white hover:shadow-[0_4px_20px_rgb(0,0,0,0.06)] transition-all duration-300"
          >
            <h2 className="text-ink font-medium text-base mb-3">{section.title}</h2>
            <p className="text-ink-secondary text-sm leading-relaxed">{section.content}</p>
          </motion.div>
        ))}

        <motion.div {...fadeUp(0.3)} className="p-6 rounded-2xl bg-brand-light border border-brand/20">
          <p className="text-ink-secondary text-sm leading-relaxed">
            <strong className="text-brand">Note:</strong> This is a placeholder privacy policy. We recommend consulting with a legal professional to create a comprehensive policy tailored to your specific operations and applicable laws (including CCPA, GDPR, or other relevant regulations).
          </p>
        </motion.div>
      </motion.div>
    </section>
  </div>
);

export default PrivacyPage;
